/**
 * The single API transport.
 *
 * Every server call in this app goes through `call()`. There is no second fetch
 * path, because the token attachment, the `{data} | {error}` unwrapping, and the
 * 4xx-vs-500 message rule all have to happen in exactly one place — a second
 * caller that "just does a quick fetch" is how a raw 500 body ends up rendered
 * to a user.
 */

const GENERIC_MESSAGE = 'Something went wrong on our side. Please try again.';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string | null;

  constructor(opts: { message: string; code: string; status: number; requestId?: string | null }) {
    super(opts.message);
    this.name = 'ApiError';
    this.code = opts.code;
    this.status = opts.status;
    this.requestId = opts.requestId ?? null;
  }

  /**
   * A business rejection the workflow intends the user to read verbatim, as
   * opposed to an infrastructure failure they can do nothing about.
   */
  get isBusiness(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /** What to actually show: the server's own wording, or a generic line + id. */
  get displayMessage(): string {
    if (this.isBusiness) return this.message;
    return this.requestId ? `${GENERIC_MESSAGE} (Reference: ${this.requestId})` : GENERIC_MESSAGE;
  }
}

let onUnauthorized: (() => void) | null = null;

/** Wired once by the auth provider; keeps this module free of React imports. */
export function configureAuth(unauthorized: () => void): void {
  onUnauthorized = unauthorized;
}

/**
 * Where to send calls.
 *
 * Empty means same-origin, which is the deployed arrangement: the host rewrites
 * `/rpc` to API Gateway server-side. That keeps the session cookie first-party —
 * the only thing that survives third-party cookie blocking — and means the
 * browser never makes a cross-origin request at all.
 *
 * An absolute URL is still supported for local development against a deployed
 * API, where same-origin is not available.
 */
function apiBase(): string {
  const base = import.meta.env.VITE_API_BASE ?? '';
  return base.replace(/\/+$/, '');
}

interface ErrorEnvelope {
  message?: unknown;
  code?: unknown;
  requestId?: unknown;
}

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

export async function call<T>(fn: string, args: object = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${apiBase()}/rpc`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The session is an HttpOnly cookie, so it is never readable from
      // JavaScript and has to be carried by the browser. Without this the
      // cookie is silently omitted cross-origin and every call reads as signed
      // out — with no error to explain why.
      credentials: 'include',
      body: JSON.stringify({ fn, args }),
    });
  } catch {
    // Network-level failure: no status, no requestId, nothing to correlate.
    throw new ApiError({
      message: 'Could not reach the server. Check your connection and try again.',
      code: 'NETWORK',
      status: 0,
    });
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const envelope = (body ?? {}) as { data?: unknown; error?: ErrorEnvelope };

  if (!response.ok || envelope.error) {
    const err = envelope.error ?? {};
    const status = response.status || 500;
    if (status === 401 || status === 403) onUnauthorized?.();
    throw new ApiError({
      message: str(err.message, GENERIC_MESSAGE),
      code: str(err.code, 'INTERNAL'),
      status,
      requestId: typeof err.requestId === 'string' ? err.requestId : null,
    });
  }

  return envelope.data as T;
}

/**
 * The allowlisted public function names, mirrored from
 * `apps/api/src/allowlist.ts`. Call sites use these instead of hand-writing the
 * strings, so a rename on the server surfaces as a compile error here rather
 * than a 400 at run time.
 */
export const api = {
  auth: {
    methods: <T>() => call<T>('auth.methods'),
    signIn: <T>(args: object) => call<T>('auth.signIn', args),
    requestOtp: <T>(args: object) => call<T>('auth.requestOtp', args),
    verifyOtp: <T>(args: object) => call<T>('auth.verifyOtp', args),
    microsoftStart: <T>(args: object = {}) => call<T>('auth.microsoftStart', args),
    microsoftComplete: <T>(args: object) => call<T>('auth.microsoftComplete', args),
    signOut: <T>() => call<T>('auth.signOut'),
    changePassword: <T>(args: object) => call<T>('auth.changePassword', args),
  },
  session: {
    me: <T>() => call<T>('session.me'),
    completeProfile: <T>(args: object) => call<T>('session.completeProfile', args),
    updateEmpId: <T>(args: object) => call<T>('session.updateEmpId', args),
  },
  directory: {
    categories: <T>() => call<T>('directory.categories'),
    approvalPreview: <T>(args: object) => call<T>('directory.approvalPreview', args),
  },
  notifications: {
    list: <T>() => call<T>('notifications.list'),
    markRead: <T>(args: object = {}) => call<T>('notifications.markRead', args),
  },
  drafts: {
    list: <T>() => call<T>('drafts.list'),
    get: <T>(args: object) => call<T>('drafts.get', args),
    save: <T>(args: object) => call<T>('drafts.save', args),
    delete: <T>(args: object) => call<T>('drafts.delete', args),
    discardAfterSubmit: <T>(args: object) => call<T>('drafts.discardAfterSubmit', args),
  },
  signature: {
    mine: <T>() => call<T>('signature.mine'),
    uploadUrl: <T>(args: object) => call<T>('signature.uploadUrl', args),
    save: <T>(args: object) => call<T>('signature.save', args),
    delete: <T>() => call<T>('signature.delete'),
    forClaim: <T>(args: object) => call<T>('signature.forClaim', args),
  },
  fx: {
    list: <T>(args: object = {}) => call<T>('fx.list', args),
    upsert: <T>(args: object) => call<T>('fx.upsert', args),
    remove: <T>(args: object) => call<T>('fx.remove', args),
  },
  counts: {
    queues: <T>() => call<T>('counts.queues'),
  },
  employeeFinance: {
    list: <T>() => call<T>('employeeFinance.list'),
    set: <T>(args: object) => call<T>('employeeFinance.set', args),
    bulkSet: <T>(args: object) => call<T>('employeeFinance.bulkSet', args),
  },
  claims: {
    mine: <T>(args: object = {}) => call<T>('claims.mine', args),
    get: <T>(args: object) => call<T>('claims.get', args),
    create: <T>(args: object) => call<T>('claims.create', args),
    resubmit: <T>(args: object) => call<T>('claims.resubmit', args),
    checkReceiptDuplicate: <T>(args: object) => call<T>('claims.checkReceiptDuplicate', args),
  },
  approvals: {
    queue: <T>(args: object = {}) => call<T>('approvals.queue', args),
    decide: <T>(args: object) => call<T>('approvals.decide', args),
  },
  // One finance desk since Round 2: review, approve and release. The retired
  // `financeApprove.*` and `treasury.*` names are gone from the server
  // allowlist too, so a stale caller gets a 400 rather than a silent no-op.
  financeReview: {
    queue: <T>(args: object = {}) => call<T>('financeReview.queue', args),
    take: <T>(args: object) => call<T>('financeReview.take', args),
    approveAndRelease: <T>(args: object) => call<T>('financeReview.approveAndRelease', args),
    return: <T>(args: object) => call<T>('financeReview.return', args),
    editDescription: <T>(args: object) => call<T>('financeReview.editDescription', args),
    setOriginalsReceived: <T>(args: object) => call<T>('financeReview.setOriginalsReceived', args),
    notFiled: <T>(args: object = {}) => call<T>('financeReview.notFiled', args),
  },
  receipts: {
    uploadUrl: <T>(args: object) => call<T>('receipts.uploadUrl', args),
    viewUrl: <T>(args: object) => call<T>('receipts.viewUrl', args),
    extract: <T>(args: object) => call<T>('receipts.extract', args),
    extractResult: <T>(args: object) => call<T>('receipts.extractResult', args),
  },
  reporting: {
    financialDashboard: <T>(args: object = {}) => call<T>('reporting.financialDashboard', args),
    pendingExport: <T>(args: object = {}) => call<T>('reporting.pendingExport', args),
  },
  admin: {
    listUsers: <T>(args: object = {}) => call<T>('admin.listUsers', args),
    addUser: <T>(args: object) => call<T>('admin.addUser', args),
    updateUser: <T>(args: object) => call<T>('admin.updateUser', args),
    setSystemAccess: <T>(args: object) => call<T>('admin.setSystemAccess', args),
    setReportsTo: <T>(args: object) => call<T>('admin.setReportsTo', args),
    deactivateUser: <T>(args: object) => call<T>('admin.deactivateUser', args),
    reactivateUser: <T>(args: object) => call<T>('admin.reactivateUser', args),
    bulkImportUsers: <T>(args: object) => call<T>('admin.bulkImportUsers', args),
    addCategory: <T>(args: object) => call<T>('admin.addCategory', args),
    renameCategory: <T>(args: object) => call<T>('admin.renameCategory', args),
    setCategoryActive: <T>(args: object) => call<T>('admin.setCategoryActive', args),
    deleteUser: <T>(args: object) => call<T>('admin.deleteUser', args),
    resetPassword: <T>(args: object) => call<T>('admin.resetPassword', args),
    auditTrail: <T>(args: object = {}) => call<T>('admin.auditTrail', args),
    listClaims: <T>(args: object = {}) => call<T>('admin.listClaims', args),
    voidClaim: <T>(args: object) => call<T>('admin.voidClaim', args),
    reassignClaim: <T>(args: object) => call<T>('admin.reassignClaim', args),
    releaseClaimOwnership: <T>(args: object) => call<T>('admin.releaseClaimOwnership', args),
    requestMainAccessTransfer: <T>(args: object) =>
      call<T>('admin.requestMainAccessTransfer', args),
    confirmMainAccessTransfer: <T>(args: object) =>
      call<T>('admin.confirmMainAccessTransfer', args),
  },
} as const;

/** Narrow an unknown thrown value into something renderable. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.displayMessage;
  if (err instanceof Error && err.message) return err.message;
  return GENERIC_MESSAGE;
}
